// ─────────────────────────────────────────────────────────────────────────────
// Checkout — the main live component.
//
// Reads the token (injected by <App /> after regex validation), runs the
// polling loop via usePolling, and renders the state-appropriate view.
//
// Rendering decision tree:
//
//   loading (first fetch hasn't returned)
//     → skeleton / minimal framing
//
//   error (terminal: CHECKOUT_NOT_FOUND)
//     → <InvalidLink /> with the BFF's message
//
//   error (transient: still polling)
//     → keep showing previous data if we have any; else a loading skeleton
//
//   data.status === 'pending'
//     → <PendingState /> — VA card, countdown, CTA
//
//   data.status === 'completed' | 'expired' | 'cancelled' | 'failed'
//     → <TerminalState /> with per-status content
//
// The outer frame (Topbar + card shell + MethodRow + MerchantRow +
// AmountCard + Footer) is identical across all states for visual continuity.
// Only the inner content after the AmountCard changes.
// ─────────────────────────────────────────────────────────────────────────────

import { usePolling } from './usePolling';
import { isTerminalError } from './types';
import { Topbar } from './components/Topbar';
import { Footer } from './components/Footer';
import { MethodRow } from './components/MethodRow';
import { MerchantRow } from './components/MerchantRow';
import { AmountCard } from './components/AmountCard';
import { PendingState } from './components/PendingState';
import { TerminalState } from './components/TerminalState';
import { InvalidLink } from './components/InvalidLink';

export interface CheckoutProps {
  token: string;
}

export function Checkout({ token }: CheckoutProps) {
  const { data, error, pollNow } = usePolling(token);

  // Terminal error: the token resolves to nothing on the server. Treat
  // exactly like a malformed URL — no recovery path from the browser.
  if (error && isTerminalError(error)) {
    return <InvalidLink message={error.message} />;
  }

  // Initial load: no data yet, no terminal error yet. Render the frame
  // with a minimal placeholder body. Transient errors during this window
  // also fall here — the user sees loading while we retry.
  if (!data) {
    return (
      <main className="shell">
        <section className="checkout-card" aria-busy="true">
          <Topbar returnUrl={null} />
          <div className="method-divider" />
          <MethodRow />
          <div className="method-divider" />
          <div className="pane">
            <div
              style={{
                minHeight: '280px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-dim)',
                fontSize: '13px',
              }}
            >
              Loading checkout…
            </div>
          </div>
          <div className="method-divider" />
          <Footer />
        </section>
      </main>
    );
  }

  // We have data. Render the frame with the appropriate inner state.
  return (
    <main className="shell">
      <section className="checkout-card" aria-live="polite">
        <Topbar returnUrl={data.returnUrl} />
        <div className="method-divider" />
        <MethodRow />
        <div className="method-divider" />
        <div className="pane">
          <MerchantRow merchantDisplayName={data.merchantDisplayName} />
          <AmountCard
            amountDisplay={data.amountDisplay}
            amountKobo={data.amountKobo}
            currency={data.currency}
          />

          {data.status === 'pending' && (
            <PendingState data={data} onTransferClaimed={pollNow} />
          )}

          {data.status !== 'pending' && (
            <TerminalState status={data.status} data={data} />
          )}
        </div>
        <div className="method-divider" />
        <Footer />
      </section>
    </main>
  );
}
